import { useState, useEffect } from "react";
import { useDidUpdate } from "@/hooks/useDidUpdate";
import { todayISO } from "@/lib/utils";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import Badge from "@/components/ui/badge/Badge";
import { Modal } from "@/components/ui/modal";
import ConfirmModal from "@/components/ui/modal/ConfirmModal";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import { mortalityLogService } from "@/services/dailyLoggingService";
import { flockService } from "@/services/masterDataService";
import { handleApiError } from "@/services/api";
import DateRangeFilter, { getDefaultDateRange } from "@/components/common/DateRangeFilter";
import type { MortalityLog, Flock } from "@/types/models";
import { useIsViewer, useIsOwner } from "@/hooks/useRole";
import { useSortable } from "@/hooks/useSortable";
import SortableHeader from "@/components/ui/table/SortableHeader";
import Pagination from "@/components/ui/pagination/Pagination";
import { MORTALITY_CAUSES } from "@/lib/mortalityCauses";

const selectClass =
  "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

const causeOptions = MORTALITY_CAUSES;

const causeColors: Record<string, "error" | "warning" | "light"> = {
  disease: "error",
  heat_stress: "warning",
  predator: "error",
  unknown: "light",
  other: "light",
};

export default function MortalityPage() {
  const isViewer = useIsViewer();
  const isOwner = useIsOwner();
  const { sortConfig, requestSort } = useSortable("date", "desc");
  const [records, setRecords] = useState<MortalityLog[]>([]);
  const [flocks, setFlocks] = useState<Flock[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const [editing, setEditing] = useState<MortalityLog | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<MortalityLog | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Filters
  const [filterFlockId, setFilterFlockId] = useState("");
  const [filterFrom, setFilterFrom] = useState(() => getDefaultDateRange().from);
  const [filterTo, setFilterTo] = useState(() => getDefaultDateRange().to);

  // Form state
  const [flockId, setFlockId] = useState("");
  const [date, setDate] = useState(todayISO());
  const [count, setCount] = useState("");
  const [cause, setCause] = useState("unknown");
  const [notes, setNotes] = useState("");

  const loadRecords = async (fromDate?: string, toDate?: string, newOffset?: number) => {
    setError("");
    try {
      const o = newOffset !== undefined ? newOffset : offset;
      const params: Record<string, string | number> = { limit, offset: o };
      if (filterFlockId) params.flock_id = parseInt(filterFlockId);
      const f = fromDate !== undefined ? fromDate : filterFrom;
      const t = toDate !== undefined ? toDate : filterTo;
      if (f) params.from = f;
      if (t) params.to = t;
      params.sort_by = sortConfig.key;
      params.sort_order = sortConfig.direction;
      const res = await mortalityLogService.list(params);
      if (Array.isArray(res)) {
        setRecords(res as unknown as MortalityLog[]);
        setTotal((res as unknown as MortalityLog[]).length);
      } else {
        const paginated = res as unknown as { data: MortalityLog[]; meta: { total: number } };
        setRecords(paginated.data ?? []);
        setTotal(paginated.meta?.total ?? 0);
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const loadFlocks = async () => {
    try {
      const data = await flockService.list();
      setFlocks((data as Flock[]).filter((f) => f.status === "active"));
    } catch {
      // silently fail
    }
  };

  useEffect(() => {
    loadFlocks();
    loadRecords();
  }, []);

  useEffect(() => {
    setLoading(true);
    loadRecords();
  }, [sortConfig]);

  const openCreate = () => {
    setEditing(null);
    setFlockId(flocks.length > 0 ? flocks[0].id.toString() : "");
    setDate(todayISO());
    setCount("");
    setCause("unknown");
    setNotes("");
    setError("");
    setModalOpen(true);
  };

  const openEdit = (rec: MortalityLog) => {
    setEditing(rec);
    setFlockId(rec.flock_id.toString());
    setDate(new Date(rec.date).toISOString().split("T")[0]);
    setCount(rec.count.toString());
    setCause(rec.cause || "unknown");
    setNotes(rec.notes || "");
    setError("");
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      const data = {
        flock_id: parseInt(flockId),
        date,
        count: parseInt(count),
        cause: cause as MortalityLog["cause"],
        notes: notes || undefined,
      };
      if (editing) {
        await mortalityLogService.update(editing.id, data);
      } else {
        await mortalityLogService.create(data);
      }
      setModalOpen(false);
      setLoading(true);
      await loadRecords();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await mortalityLogService.delete(deleteConfirm.id);
      setDeleteConfirm(null);
      setLoading(true);
      await loadRecords();
    } catch (err) {
      setError(handleApiError(err));
      setDeleteConfirm(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleDateRangeApply = (from: string, to: string) => {
    setFilterFrom(from);
    setFilterTo(to);
    setOffset(0);
    setLoading(true);
    loadRecords(from, to, 0);
  };

  const handleFilter = () => {
    setOffset(0);
    setLoading(true);
    loadRecords(undefined, undefined, 0);
  };

  // A dropdown applies the moment it changes; there is no separate Filter button.
  useDidUpdate(handleFilter, [filterFlockId]);

  const handlePrev = () => {
    const newOffset = Math.max(0, offset - limit);
    setOffset(newOffset);
    loadRecords(undefined, undefined, newOffset);
  };

  const handleNext = () => {
    const newOffset = offset + limit;
    setOffset(newOffset);
    loadRecords(undefined, undefined, newOffset);
  };

  const formatCause = (c?: string) => {
    if (!c) return "-";
    return c.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <>
      <PageMeta title="Mortality | Poultry Book" description="Daily mortality logging" />
      <PageBreadcrumb pageTitle="Mortality" />

      <div className="card">
        <div className="flex items-center justify-between px-5 py-4 sm:px-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Mortality Logs {total > 0 && <span className="text-sm font-normal text-gray-500">({total})</span>}
          </h3>
          {!isViewer && (
            <Button size="sm" onClick={openCreate}>
              Log Mortality
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 px-5 pb-4 sm:px-6">
          <select
            value={filterFlockId}
            onChange={(e) => setFilterFlockId(e.target.value)}
            className={`${selectClass} max-w-[200px]`}
          >
            <option value="">All Flocks</option>
            {flocks.map((f) => (
              <option key={f.id} value={f.id}>
                {f.batch_name}
              </option>
            ))}
          </select>
          <DateRangeFilter onApply={handleDateRangeApply} />
        </div>

        {error && !modalOpen && (
          <div className="mx-5 mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500 sm:mx-6">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          {loading ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              Loading...
            </div>
          ) : records.length === 0 && !error ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              No mortality records in the selected period.
            </div>
          ) : records.length === 0 ? null : (
            <Table>
              <TableHeader>
                <TableRow className="border-t border-gray-200 dark:border-gray-800">
                  <SortableHeader label="Date" sortKey="date" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader label="Flock/Shed" sortKey="flock" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader label="Count" sortKey="count" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader label="Cause" sortKey="cause" sortConfig={sortConfig} onSort={requestSort} />
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Notes
                  </TableCell>
                  {!isViewer && (
                    <TableCell isHeader className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((rec) => (
                  <TableRow key={rec.id} className="border-t border-gray-100 dark:border-gray-800">
                    <TableCell className="px-5 py-3 text-sm text-gray-800 dark:text-white/90">
                      {new Date(rec.date).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {rec.flock_name}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-right font-medium text-error-600 dark:text-error-400">
                      {rec.count}
                    </TableCell>
                    <TableCell className="px-5 py-3">
                      {rec.cause ? (
                        <Badge size="sm" color={causeColors[rec.cause] ?? "light"}>
                          {formatCause(rec.cause)}
                        </Badge>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {rec.notes || "-"}
                    </TableCell>
                    {!isViewer && (
                      <TableCell className="px-5 py-3 text-right">
                        <button
                          onClick={() => openEdit(rec)}
                          className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                        >
                          Edit
                        </button>
                        {isOwner && (
                          <button
                            onClick={() => setDeleteConfirm(rec)}
                            className="ml-3 text-sm text-error-500 hover:text-error-600 dark:text-error-400"
                          >
                            Delete
                          </button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <Pagination total={total} limit={limit} offset={offset} onPrev={handlePrev} onNext={handleNext} />
      </div>

      {/* Create Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} className="max-w-md p-5 sm:p-8 mx-4">
        <h4 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90">
          {editing ? "Edit Mortality Log" : "Log Mortality"}
        </h4>

        {error && modalOpen && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="date">Date *</Label>
            <Input
              id="date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="flockId">Flock *</Label>
            <select
              id="flockId"
              value={flockId}
              onChange={(e) => setFlockId(e.target.value)}
              required
              className={selectClass}
            >
              <option value="">Select flock</option>
              {flocks.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.batch_name} ({f.shed_code})
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="count">Count *</Label>
            <Input
              id="count"
              type="number"
              value={count}
              onChange={(e) => setCount(e.target.value)}
              placeholder="Number of birds"
              required
              min={1}
            />
          </div>

          <div>
            <Label htmlFor="cause">Cause</Label>
            <select
              id="cause"
              value={cause}
              onChange={(e) => setCause(e.target.value)}
              className={selectClass}
            >
              {causeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="notes">Notes</Label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
              rows={2}
              className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" size="sm" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={saving || !flockId || !count}>
              {saving ? "Saving..." : editing ? "Update" : "Log Mortality"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteConfirm}
        title="Delete Entry"
        message={
          <>
            Are you sure you want to delete the mortality entry for{" "}
            <span className="font-medium">{deleteConfirm && new Date(deleteConfirm.date).toLocaleDateString()}</span>{" "}
            ({deleteConfirm?.count} {deleteConfirm?.count === 1 ? "bird" : "birds"})? The flock's current count will be restored. This action cannot be undone.
          </>
        }
        busy={deleting}
        busyLabel="Deleting…"
        onConfirm={handleDelete}
        onClose={() => setDeleteConfirm(null)}
      />
    </>
  );
}
